// Orchestrates a completed snip: extracted text -> mode plan -> Excel write -> record.
import type { NormRect, RegionText } from '../types'
import { state, addSnippet, activeDocument } from './store'
import { buildPlan, MODE_LABELS } from './modes'
import { writeSnip } from './excel'
import { recordSnip } from './telemetry'

export interface SnipPayload {
  text: string
  source: RegionText['source']
  rect: NormRect
  pageNumber: number
}

function displayName(text: string): string {
  const firstLine = text.split('\n')[0]?.trim() ?? ''
  return firstLine.length > 40 ? firstLine.slice(0, 40) + '…' : firstLine || '(empty)'
}

export async function commitSnip(payload: SnipPayload): Promise<void> {
  const doc = activeDocument()
  if (!doc) return

  if (!payload.text.trim()) {
    state.status = 'No text found in selection.'
    return
  }

  state.busy = true
  try {
    const plan = buildPlan(state.mode, payload.text, state.euNotation)

    if (plan.kind === 'skip') {
      state.status = plan.message
      return
    }

    let cellAddress = '(preview)'
    let worksheetName = '(preview)'
    let worksheetId: string | undefined

    if (state.insideOffice) {
      const result = await writeSnip(plan)
      cellAddress = result.address
      worksheetName = result.worksheetName
      worksheetId = result.worksheetId
    }

    addSnippet({
      documentId: doc.id,
      documentName: doc.name,
      pageNumber: payload.pageNumber,
      rect: payload.rect,
      mode: state.mode,
      text: payload.text,
      displayName: displayName(payload.text),
      cellAddress,
      worksheetName,
      worksheetId,
      rotation: state.rotation,
    })
    recordSnip(state.mode)

    state.status = state.insideOffice
      ? `${MODE_LABELS[state.mode]} snipped to ${cellAddress} (${payload.source})`
      : `${MODE_LABELS[state.mode]} captured (${payload.source}) — open in Excel to write to a cell`
  } catch (err) {
    state.status = `Snip failed: ${(err as Error).message}`
  } finally {
    state.busy = false
  }
}
